import React from "react";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { buildWhatsAppLink } from "@/lib/whatsapp";

export default function Contact() {
    const { settings } = useSettings();

    const cards = [
        { icon: MessageCircle, label: "WhatsApp", value: settings.phone_display, href: buildWhatsAppLink(settings.whatsapp_number, "Hello Gold by SRN — I'd like to enquire."), testid: "contact-whatsapp", cta: "Chat now", brand: true },
        { icon: Phone, label: "Call", value: settings.phone_display, href: `tel:${settings.phone_display.replace(/\s/g, "")}`, testid: "contact-call", cta: "Place a call" },
        { icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}`, testid: "contact-email", cta: "Write to us" },
    ];

    return (
        <div className="bg-paper min-h-screen">
            <section className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-20 pb-12 text-center border-b border-soft">
                <p className="overline">Get in touch</p>
                <h1 className="font-serif text-5xl sm:text-7xl text-ink mt-4">We're a message away.</h1>
                <p className="mt-6 text-charcoal max-w-2xl mx-auto">Talk to us about new collections, bespoke designs, or simply to introduce your shop. Trade enquiries only — no retail sales.</p>
            </section>

            <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-3 gap-5">
                {cards.map((c) => (
                    <a
                        key={c.label}
                        href={c.href}
                        target={c.label === "WhatsApp" ? "_blank" : undefined}
                        rel="noreferrer"
                        data-testid={c.testid}
                        className={`group block p-8 transition-colors text-center ${c.brand ? 'bg-[#25D366] text-white hover:bg-[#1DA851]' : 'bg-ivory hover:bg-ink hover:text-paper'}`}
                    >
                        <c.icon className={c.brand ? 'text-white mx-auto' : 'text-bronze-deep mx-auto group-hover:text-paper'} size={22} />
                        <p className={`overline mt-6 ${c.brand ? '!text-white/85' : 'group-hover:!text-paper/80'}`}>{c.label}</p>
                        <p className={`font-serif text-2xl mt-2 ${c.brand ? 'text-white' : 'text-ink group-hover:text-paper'}`}>{c.value}</p>
                        <p className={`mt-6 text-[10px] uppercase tracking-[0.24em] ${c.brand ? 'text-white' : 'text-bronze-deep group-hover:text-paper'}`}>{c.cta} →</p>
                    </a>
                ))}
            </section>

            <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pb-24 grid md:grid-cols-2 gap-5">
                <div className="bg-ivory p-8">
                    <p className="overline">Atelier</p>
                    <ul className="mt-6 space-y-5 text-sm">
                        <li className="flex items-start gap-3"><MapPin size={16} className="text-bronze-deep mt-0.5" /><span>{settings.address}</span></li>
                        <li className="flex items-start gap-3"><Clock size={16} className="text-bronze-deep mt-0.5" /><span>{settings.business_hours}</span></li>
                        {settings.instagram && (
                            <li className="flex items-start gap-3"><Instagram size={16} className="text-bronze-deep mt-0.5" />
                                <a href={settings.instagram} target="_blank" rel="noreferrer" className="underline-grow">@goldbysrn</a>
                            </li>
                        )}
                    </ul>
                </div>
                <div className="bg-ivory p-8">
                    <p className="overline">For jewellers</p>
                    <p className="font-serif text-2xl text-ink mt-4 leading-snug">
                        "We work the way the trade prefers — one direct, honest conversation."
                    </p>
                    <p className="mt-5 text-sm text-charcoal leading-relaxed">
                        Send us your enquiry through WhatsApp and you'll be talking to a member of our family, not a call-centre. We share current rates, lead-times and sample dispatch details — all in one chat.
                    </p>
                </div>
            </section>
        </div>
    );
}
